import { Suspense, useMemo, useRef } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import * as THREE from "three";
import { motion, useScroll, useTransform } from "framer-motion";

/**
 * Restrained 3D background for the hero:
 *   - 24 monochrome wireframe "candlesticks" on a grid (deterministic seed)
 *   - very slow auto-rotation
 *   - mouse parallax on tilt
 *   - scroll-driven rotation
 *
 * Strict palette: only #E5E4E2 (platinum), pure black background, no gradients.
 */

// Deterministic pseudo-random — keeps the layout stable across renders/builds
function pseudo(seed) {
  let t = seed * 9301 + 49297;
  return () => {
    t = (t * 9301 + 49297) % 233280;
    return t / 233280;
  };
}

function CandleField({ scrollProgress }) {
  const groupRef = useRef();
  const COLUMNS = 12;
  const SPACING = 0.95;

  const candles = useMemo(() => {
    const rand = pseudo(42);
    const items = [];
    let lastTop = 0.4;
    for (let i = 0; i < COLUMNS; i++) {
      // Random walk so it looks like real OHLC, not noise
      const drift = (rand() - 0.45) * 1.4;
      const top = lastTop + drift;
      const bottom = top - (0.2 + rand() * 1.5);
      const open = bottom + (top - bottom) * (0.25 + rand() * 0.5);
      const close = bottom + (top - bottom) * (0.25 + rand() * 0.5);
      const bodyTop = Math.max(open, close);
      const bodyBottom = Math.min(open, close);
      const wickHeight = top - bottom;
      const wickY = (top + bottom) / 2;
      const bodyHeight = Math.max(bodyTop - bodyBottom, 0.08);
      const bodyY = (bodyTop + bodyBottom) / 2;
      items.push({
        x: (i - (COLUMNS - 1) / 2) * SPACING,
        wickHeight,
        wickY,
        bodyHeight,
        bodyY,
      });
      lastTop = top;
    }
    return items;
  }, []);

  useFrame((state) => {
    if (!groupRef.current) return;
    const t = state.clock.elapsedTime;
    const sp = scrollProgress.current ?? 0;
    // Slow continuous spin + scroll-coupled tilt
    groupRef.current.rotation.y = t * 0.05 + sp * 0.6;
    groupRef.current.rotation.x = -0.25 + sp * 0.3;
    // Mouse parallax (state.pointer is normalised [-1, 1])
    const px = state.pointer.x * 0.12;
    const py = state.pointer.y * 0.08;
    groupRef.current.rotation.y += px;
    groupRef.current.rotation.x += py;
  });

  const lineMat = useMemo(
    () =>
      new THREE.LineBasicMaterial({
        color: "#E5E4E2",
        transparent: true,
        opacity: 0.55,
        depthWrite: false,
      }),
    []
  );
  const lineMatFaint = useMemo(
    () =>
      new THREE.LineBasicMaterial({
        color: "#E5E4E2",
        transparent: true,
        opacity: 0.22,
        depthWrite: false,
      }),
    []
  );

  return (
    <group ref={groupRef}>
      {/* Baseline grid floor */}
      <gridHelper
        args={[14, 14, "#E5E4E2", "#2A2A2A"]}
        position={[0, -3.2, 0]}
        rotation={[0, 0, 0]}
      />

      {candles.map((c, i) => {
        const bodyEdges = new THREE.EdgesGeometry(
          new THREE.BoxGeometry(0.45, c.bodyHeight, 0.45)
        );
        const wickEdges = new THREE.EdgesGeometry(
          new THREE.BoxGeometry(0.04, c.wickHeight, 0.04)
        );
        return (
          <group key={i} position={[c.x, 0, 0]}>
            <lineSegments
              geometry={wickEdges}
              material={lineMatFaint}
              position={[0, c.wickY, 0]}
            />
            <lineSegments
              geometry={bodyEdges}
              material={lineMat}
              position={[0, c.bodyY, 0]}
            />
          </group>
        );
      })}
    </group>
  );
}

export default function Hero3D() {
  const { scrollY } = useScroll();
  const scrollProgress = useRef(0);
  useTransform(scrollY, (v) => {
    // Normalise to 0..1 within first viewport height
    const h = typeof window !== "undefined" ? window.innerHeight : 1;
    scrollProgress.current = Math.min(Math.max(v / h, 0), 1);
    return v;
  });

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 0.65 }}
      transition={{ duration: 1.6, ease: [0.16, 1, 0.3, 1], delay: 0.4 }}
      aria-hidden
      className="absolute inset-0 pointer-events-none"
    >
      <Canvas
        camera={{ position: [0, 0.6, 9], fov: 38 }}
        dpr={[1, 1.5]}
        gl={{ antialias: true, alpha: true }}
        style={{ background: "transparent" }}
      >
        <ambientLight intensity={0.4} />
        <Suspense fallback={null}>
          <CandleField scrollProgress={scrollProgress} />
        </Suspense>
      </Canvas>
    </motion.div>
  );
}
